import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsMagic } from "react-icons/bs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useGetAllProjectsQuery } from "../../../redux/slices/projectApiSlice";
import { InputProfile } from "../../../shared/components/profile";
import { useGenerationErrorHandling } from "../../../shared/utils/errorHandling";
import { trackPlatformUsed } from "../../../shared/utils/trackers";
import ManualGV from "./ManualGV";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Pour supprimer le séparateur
const NoSeparator = () => null;
// Pour centrer le favicon dans la valeur sélectionnée
const CenteredSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      {props.children}
    </div>
  </components.SingleValue>
);

const DefaultGV = ({
  counter,
  setCounter,
  variable,
  loadingVariable,
  singleVariable,
  updateVariable,
  loadingUpdate,
  linkVariableToWebsite,
  variables,
  refetch,
  deleteVariable,
  loadingDelete,
  analyzeVariable,
  loadingAnalyze,
  createVariable,
  loadingCreate,
  variableId,
  variableTitle,
  variableNameAndPronoun,
  variableImage,
  variableFull,
  initialValues,
  validationSchema,
  manualText,
  manualBtnText,
  autoText,
  autoBtnText,
  bulkDeleteVariables,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth.userInfo);
  const navigate = useNavigate();

  const [onEdit, setOnEdit] = useState(false);
  const [editVariable, setEditVariable] = useState(null);
  const [open, setOpen] = useState(false);
  const [deletionType, setDeletionType] = useState("single");
  const [selectedId, setSelectedId] = useState(null);
  const [loadingDeleteModal, setLoadingDeleteModal] = useState(false);

  const [isCustom, setIsCustom] = useState(false);
  const [selectId, setSelectId] = useState("");
  const [value, setValue] = useState("Automatic");

  const handleGenerationError = useGenerationErrorHandling();

  const { data: projects } = useGetAllProjectsQuery();
  const currentProject = useSelector((state) => state?.project);

  const [selectedVariables, setSelectedVariables] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [menuPlacements, setMenuPlacements] = useState({});
  const selectRefs = useRef({});

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  const tabs = [
    { name: t("auto", "Automatique"), value: "Automatic" },
    { name: t("manual", "Manuel"), value: "Manual" },
  ];

  // Create variable from inputs automatically
  const handleCreateVariableAuto = async (website_url, props) => {
    trackPlatformUsed("generation_" + variableId, "ai_analysis");
    try {
      if (website_url) {
        const res = await analyzeVariable({ website_url: website_url });
        if (res.data.status === 201) {
          props.setFieldValue(variableFull, res.data[variableFull]);
          toast.success(
            `${
              variableId === "company" ? "L'" : "Le "
            }${variableTitle.toLowerCase()} a bien été analysé !`
          );
        }
      } else {
        toast.error(
          t(
            "you-must-provide-link",
            "Vous devez renseigner un lien pour effectuer une analyse automatique !"
          )
        );
      }
    } catch (error) {
      handleGenerationError(error);
    }
  };

  // Create variable from inputs manually
  const handleCreateVariableManual = async (props) => {
    trackPlatformUsed("generation_" + variableId, "concatenation");
    try {
      const res = await createVariable(props.values);
      if (res.data.status === 201) {
        props.setFieldValue(variableFull, res.data[variableFull]);
        toast.success(
          `${
            variableId === "company" ? "L'" : "Le "
          }${variableTitle.toLowerCase()} a bien été créée !`
        );
        setOnEdit(false);
        setEditVariable(null);
      }
    } catch (error) {
      toast.error(error.error || "Something went wrong");
    }
  };

  // To open the delete modal
  const handleDeleteVariable = (type, id = null) => {
    setDeletionType(type);
    setOpen(true);
    if (type === "single") {
      setSelectedId(id);
    }
  };

  const handleDelete = async () => {
    setLoadingDeleteModal(true);
    try {
      if (deletionType === "single" && selectedId) {
        await deleteVariable(selectedId);
        toast.success(
          t(
            `${variableId}-deleted-succesfully`,
            `${variableTitle} supprimé(e) !`
          )
        );
      } else if (
        deletionType === "selected" &&
        bulkDeleteVariables &&
        selectedVariables.size > 0
      ) {
        await bulkDeleteVariables(Array.from(selectedVariables));
        setSelectedVariables(new Set());
        toast.success("Suppression groupée réussie !");
      }
      setOnEdit(false);
      setEditVariable(null);
      refetch();
      setOpen(false);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      setOpen(false);
    } finally {
      setLoadingDeleteModal(false);
    }
  };

  // To edit the variable
  const handleVariableEdit = async (id) => {
    if (id) {
      try {
        setOnEdit(true);
        const res = await singleVariable({ id }).unwrap();
        const sanitizedData = Object.fromEntries(
          Object.entries(res?.data || {}).map(([key, value]) => [
            key,
            value === null && key != "website_id" ? "" : value,
          ])
        );
        setEditVariable(sanitizedData);
      } catch (error) {}
    }
  };

  const handleCreateNew = async (resetForm) => {
    setOnEdit(false);
    setEditVariable(null);
    if (variableFull === "writing_style_full") {
      setIsCustom(false);
    }
    refetch();
    resetForm();
  };

  // Placeholder pour la mutation de rattachement à un site web
  const handleLinkVariableToWebsite = async (selectedProject, variableId) => {
    try {
      const projectId = selectedProject?.value ? selectedProject.value : null;
      const result = await linkVariableToWebsite({ projectId, variableId });
      console.log(result);
      if (result?.status == 404) {
        toast.error(result.error);
      }
      refetch();
      toast.info(
        projectId
          ? `${variableNameAndPronoun} a été rattaché(e) au site web`
          : `${variableNameAndPronoun} a été détaché(e) du site web`
      );
    } catch (error) {
      console.error("Error selecting project:", error);
    }
  };

  const handleMenuOpen = (variableId) => () => {
    const ref = selectRefs.current[variableId];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuPlacements((prev) => ({
        ...prev,
        [variableId]: spaceBelow < 220 ? "top" : "auto",
      }));
    }
  };

  const customStyles = {
    menu: (base) => ({
      ...base,
      maxHeight: "200px",
      minWidth: "200px",
      margin: 0,
      padding: 0,
      zIndex: 9999,
      marginTop: "6px",
      marginBottom: "8px",
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      margin: 0,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      maxHeight: "200px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      padding: 0,
      margin: 0,
    }),
  };

  // Gestion des checks
  const isAllSelected =
    variables &&
    variables.length > 0 &&
    variables.every((v) => selectedVariables.has(v.id));
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedVariables(new Set(variables.map((v) => v.id)));
    } else {
      setSelectedVariables(new Set());
    }
  };
  const handleCheckboxChange = (id) => {
    setSelectedVariables((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  const handleApplyBulkAction = () => {
    if (bulkAction === "delete") {
      handleDeleteVariable("selected");
    }
  };

  // Options ReactSelect
  const projectOptions = [
    {
      value: null,
      label: (
        <div className="flex items-center gap-2">
          <div className="font-bold text-sm">
            {t("Unselect", "Désélectionner")}
          </div>
        </div>
      ),
    },
    ...(projects?.data?.map((project) => ({
      value: project.id,
      label: (
        <div className="flex items-center gap-2">
          <img src={project.favicon} alt={project.name} className="w-6 h-6" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{project.name}</div>
            <div
              className="text-xs text-gray-500 truncate overflow-hidden whitespace-nowrap"
              title={project.url}
            >
              {project.url}
            </div>
          </div>
        </div>
      ),
    })) || []),
  ];

  return (
    <div className="bg-white py-10 rounded-2xl px-6 mx-auto shadow-default items-center ">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="mx-auto w-full flex items-center justify-center">
          <div className="grid md:grid-cols-1 gap-y-5 w-full">
            {/* TITLE */}
            <div className="flex items-center">
              {variableId === "style" ? (
                <img src={variableImage} width={35} height={35} />
              ) : (
                <BusinessIcon sx={{ width: "35px", height: "35px" }} />
              )}

              <p className="md:text-[28px] sm:text-lg font-medium ml-3">
                {variableTitle}
              </p>
            </div>

            {/* SELECT MANUAL OR AUTO */}
            <div>
              <div className="sm:hidden">
                <select
                  id="tabs"
                  name="tabs"
                  value={value}
                  onChange={(e) => handleChange(e.target.value)}
                  className="block w-full bg-blue-50 text-white rounded-md border-gray-300 focus:outline-none p-2"
                >
                  {tabs.map((tab) => (
                    <option key={tab.value} value={tab.value}>
                      {tab.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:block">
                <nav
                  aria-label="Tabs"
                  className="isolate flex divide-x divide-gray-200 rounded-lg shadow w-full"
                >
                  {tabs.map((tab, tabIdx) => (
                    <button
                      key={tab.value}
                      onClick={() => handleChange(tab.value)}
                      className={classNames(
                        tab.value === value
                          ? "text-blue-50 border-b border border-blue-50 hover:bg-ligh_gray"
                          : "text-slate-500 hover:text-slate-700 disabled:bg-slate-200 disabled:text-slate-400",
                        tabIdx === 0 ? "rounded-l-lg" : "",
                        tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                        "group relative min-w-0 flex-1 overflow-hidden bg-ligh_gray px-4 py-4 text-center text-sm font-medium hover:bg-black/10"
                      )}
                    >
                      <span>{tab.name}</span>
                      <span
                        aria-hidden="true"
                        className={classNames(
                          tab.value === value ? "bg-blue-50" : "bg-transparent",
                          "absolute inset-x-0 bottom-0 h-0.5"
                        )}
                      />
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <Formik
              initialValues={editVariable ? editVariable : initialValues}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                try {
                  if (onEdit) {
                    trackPlatformUsed(
                      "generation_" + variableId,
                      "update_in_db"
                    );
                    const res = await updateVariable(values).unwrap();
                    if (res?.status === 200) {
                      toast.success(
                        t(
                          `${variableId}-updated-succesfully`,
                          `Votre ${variableTitle.toLowerCase()} a bien été modifié !`
                        )
                      );
                      setCounter(counter + 1);
                    }
                  } else {
                    trackPlatformUsed(
                      "generation_" + variableId,
                      "create_in_db"
                    );
                    const res = await variable(values).unwrap();
                    setCounter((prev) => prev + 1);
                    if (res?.status === 201) {
                      toast.success(
                        t(
                          `${variableId}-created-succesfully`,
                          `Votre ${variableTitle.toLowerCase()} a bien été créé !`
                        )
                      );
                      handleVariableEdit(res.data.id);
                    }
                  }
                } catch (error) {
                  toast?.error(error?.data?.detail);
                }
              }}
            >
              {(props) => (
                <form onSubmit={props.handleSubmit} className="grid gap-y-4">
                  {/* CREATE */}
                  <div id={variableId + "-create"}>
                    {/* AUTO */}
                    {value === "Automatic" && (
                      <div className="grid gap-y-5 w-full bg-ligh_gray rounded-xl px-2 py-3">
                        <div className="text-sm">{autoText}</div>

                        <div>
                          <InputProfile
                            formikProps={props}
                            type="text"
                            text={t("website-url", "URL de votre site web")}
                            name="website_url_scrape"
                            placeholder={t(
                              "https-my-website-fr",
                              "https://mon-site.com"
                            )}
                          />

                          <div className="flex justify-end">
                            <LoadingButton
                              startIcon={<BsMagic />}
                              loading={loadingAnalyze}
                              disabled={!userInfo.openai_api_key}
                              onClick={() =>
                                handleCreateVariableAuto(
                                  props.values.website_url_scrape,
                                  props
                                )
                              }
                              variant="contained"
                              sx={{
                                textTransform: "none",
                                bgcolor: "#8b5cf6",
                                "&:hover": {
                                  bgcolor: "#7c3aed",
                                },
                              }}
                            >
                              {autoBtnText}
                            </LoadingButton>
                          </div>

                          <div className="flex justify-end">
                            {!userInfo.openai_api_key && (
                              <p className="text-sm">
                                <span
                                  onClick={() =>
                                    navigate("/profile?tab=integrations")
                                  }
                                  className="text-blue-500 text-sm hover:underline cursor-pointer"
                                >
                                  OpenAI
                                </span>
                                {t("is-required", " est requis")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MANUAL */}
                    {value === "Manual" && (
                      <ManualGV
                        props={props}
                        name={variableId}
                        text={manualText}
                        buttonText={manualBtnText}
                        handleCreateVariableManual={handleCreateVariableManual}
                        loadingCreate={loadingCreate}
                        isCustom={isCustom}
                        setIsCustom={setIsCustom}
                      />
                    )}
                  </div>

                  {/* NAME AND CONTENT */}
                  <div id={variableId + "-content"} className="grid gap-2">
                    <div className="w-full">
                      <InputProfile
                        formikProps={props}
                        type="text"
                        text={t(
                          `${variableId}-key`,
                          `Nom ${
                            variableId === "company" ? "de l'" : "du "
                          }${variableTitle.toLowerCase()} (utilisé pour vous permettre de l'identifier)`
                        )}
                        name="key"
                        placeholder={t("example-key", "Ex: BotBlogR blog")}
                      />
                    </div>

                    <div className="flex items-center gap-9">
                      <label className="relative cursor-pointer block w-fit">
                        <input
                          className="peer sr-only"
                          defaultValue=""
                          type="checkbox"
                          checked={props.values.is_default}
                          onChange={props.handleChange}
                          name="is_default"
                        />
                        <div className="peer h-[14px] w-9 rounded-full bg-gray_local-400 after:absolute after:top-[-3px] after:left-0 after:h-5 after:w-5 after:rounded-full after:shadow-2xl after:bg-[#e1e1e1] after:transition-all after:content-[''] peer-checked:bg-[#316694] peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none" />
                      </label>
                      <p className="text-sm">
                        {t(
                          `choose-as-default-${variableId}`,
                          `Choisir comme ${variableTitle.toLowerCase()} par défaut`
                        )}
                      </p>
                    </div>

                    <div className="mt-4">
                      <InputProfile
                        formikProps={props}
                        type="text"
                        text={`${variableTitle} complet (utilisé pour la génération)`}
                        name={variableFull}
                        placeholder={`Votre ${variableTitle.toLowerCase()} détaillé ...`}
                        multiline={true}
                        maxHeight={256}
                        maxLength={20000}
                      />
                    </div>
                  </div>

                  {/* BUTTONS */}
                  <div
                    id={variableId + "-buttons"}
                    className="w-full flex justify-between items-center gap-x-4"
                  >
                    <button
                      className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white rounded font-light"
                      onClick={props.handleSubmit}
                      disabled={loadingVariable || loadingUpdate}
                    >
                      {loadingVariable || loadingUpdate ? (
                        <ClipLoader size={25} color="white" />
                      ) : (
                        t("save-all-changes", "Sauvegarder les changements")
                      )}
                    </button>
                    <button
                      type="button"
                      className="px-5 py-[6px] hover:bg-success_hover bg-success text-white rounded font-light"
                      onClick={() => handleCreateNew(props.resetForm)}
                    >
                      {t(
                        `create-new-${variableId}`,
                        `Créer un nouveau ${variableTitle.toLowerCase()}`
                      )}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>

        <div className="container">
          <div className="relative overflow-x-auto">
            {/* Bulk actions */}
            <div className="flex space-x-4 mb-2">
              <select
                className="border rounded p-2 bg-white"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">{t("bulk-action", "Actions groupées")}</option>
                <option value="delete">{t("delete", "Supprimer")}</option>
              </select>
              <button
                className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white disabled:bg-slate-200 disabled:text-slate-400 rounded font-light"
                onClick={handleApplyBulkAction}
                disabled={selectedVariables.size === 0}
              >
                {t("apply", "Appliquer")}
              </button>
            </div>
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 ">
              <thead className="text-xs text-white uppercase bg-blue-100">
                <tr>
                  <th className="px-6 py-3 rounded-s-md text-base">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-base">{t("name", "Nom")}</th>
                  <th className="px-6 py-3 text-base">
                    {t("website", "Site web")}
                  </th>
                  <th className="px-6 py-3 text-base">
                    {t("default", "Par défaut")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {variables?.map((item) => (
                  <tr
                    key={`${variableId}-${item.id}`}
                    className="odd:bg-white even:bg-black/10 group transition-all"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words">
                      <input
                        type="checkbox"
                        checked={selectedVariables.has(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words text-center">
                      <div
                        ref={(el) => {
                          selectRefs.current[item.id] = el;
                        }}
                      >
                        <Select
                          options={projectOptions}
                          value={
                            item.website_id && projects?.data
                              ? {
                                  value: projects.data.find(
                                    (obj) => obj.id === item.website_id
                                  )?.id,
                                  label: (
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={
                                          projects.data.find(
                                            (obj) => obj.id === item.website_id
                                          )?.favicon
                                        }
                                        alt={
                                          projects.data.find(
                                            (obj) => obj.id === item.website_id
                                          )?.name
                                        }
                                        className="w-6 h-6"
                                      />
                                    </div>
                                  ),
                                }
                              : null
                          }
                          styles={customStyles}
                          components={{
                            IndicatorSeparator: NoSeparator,
                            SingleValue: CenteredSingleValue,
                          }}
                          menuPlacement={menuPlacements[item.id] || "auto"}
                          menuPosition="fixed"
                          menuPortalTarget={document.body}
                          onMenuOpen={handleMenuOpen(item.id)}
                          onChange={(selectedOption) =>
                            handleLinkVariableToWebsite(selectedOption, item.id)
                          }
                          isSearchable={false}
                          placeholder={"Aucun"}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 break-words min-w-[50%] relative">
                      <div className="flex flex-col gap-2">
                        {item.key}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-4 text-blue-100 text-xs mt-1">
                          <button
                            onClick={() => handleVariableEdit(item?.id)}
                            className="hover:underline flex items-center gap-1"
                          >
                            <EditIcon size={16} /> {t("Edit", "Modifier")}
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteVariable("single", item?.id)
                            }
                            className="hover:underline flex items-center gap-1 text-red-500"
                          >
                            <DeleteIcon size={16} /> {t("Delete", "Supprimer")}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.is_default &&
                        item.website_id ===
                          (currentProject?.project?.id
                            ? currentProject?.project?.id
                            : null) && (
                          <span className="inline-block bg-blue-100 text-white text-xs px-2 py-1 rounded-full">
                            Par défaut
                          </span>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {deletionType === "single"
                        ? t("delete-variable", `Supprimer la variable`)
                        : t(
                            "delete-selected-variables",
                            "Supprimer les variables sélectionnées"
                          )}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  {deletionType === "single"
                    ? t(
                        `are-you-sure-to-delete-${variableId}`,
                        `Êtes-vous sûr de vouloir supprimer cette variable ? Les données seront effacées et vous ne pourrez plus les récupérer.`
                      )
                    : t(
                        `are-you-sure-to-delete-selected-${variableId}`,
                        `Êtes-vous sûr de vouloir supprimer ces variables ? Les données seront effacées et vous ne pourrez plus les récupérer.`
                      )}
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex w-full justify-center rounded-md bg-error px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    disabled={loadingDeleteModal}
                  >
                    {loadingDeleteModal ? (
                      <ClipLoader size={18} color="white" />
                    ) : (
                      t("delete", "Supprimer")
                    )}
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => setOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    {t("cancel", "Annuler")}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DefaultGV;
